import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, Save, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    site_name: 'BuyIt.il',
    contact_email: 'buyit.office@gmail.com',
    contact_phone_1: '+972 52 357 0058',
    contact_phone_2: '+972 53 221 4177',
    logo_url: '',
    hero_image_url: '',
    team_image_url: '',
    facebook_url: '',
    instagram_url: '',
    linkedin_url: '',
    twitter_url: '',
    whatsapp_phone: ''
  });
  
  const [collaborators, setCollaborators] = useState([
    {
      id: 1,
      name: 'Vanessa Kakun',
      name_he: 'ונסה קקון',
      name_ru: 'Ванесса Какун',
      role: 'Director',
      role_fr: 'Directrice',
      role_he: 'מנהלת',
      role_ru: 'Директор',
      phone: '+972 52 357 0058',
      email: 'buyit.office@gmail.com',
      photo: ''
    },
    {
      id: 2,
      name: 'Avitar Ben Zion',
      name_he: 'אביתר בן ציון',
      name_ru: 'Авитар Бен Цион',
      role: 'Real Estate Consultant',
      role_fr: 'Conseiller Immobilier',
      role_he: 'יועץ נדל״ן',
      role_ru: 'Консультант по Недвижимости',
      phone: '+972 53 221 4177',
      email: '',
      photo: ''
    },
    {
      id: 3,
      name: 'Nick Blum',
      name_he: 'ניק בלום',
      name_ru: 'Ник Блум',
      role: 'Real Estate Agent',
      role_fr: 'Agent Immobilier',
      role_he: 'סוכן נדל״ן',
      role_ru: 'Агент по Недвижимости',
      phone: '+972 50 277 7301',
      email: 'buyit.nb@gmail.com',
      photo: ''
    }
  ]);

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/admin/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSettings({ ...settings, [field]: response.data.url });
      toast.success('Image uploadée avec succès !');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
      console.error('Upload error:', error);
    }
  };

  const handleCollaboratorPhotoUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/admin/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const newCollaborators = [...collaborators];
      newCollaborators[index].photo = response.data.url;
      setCollaborators(newCollaborators);
      toast.success('Photo uploadée !');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${BACKEND_URL}/api/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Save collaborators to localStorage for now
      localStorage.setItem('collaborators', JSON.stringify(collaborators));

      toast.success('Paramètres sauvegardés avec succès !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addCollaborator = () => {
    setCollaborators([...collaborators, {
      id: Date.now(),
      name: '',
      name_he: '',
      name_ru: '',
      role: '',
      role_fr: '',
      role_he: '',
      role_ru: '',
      phone: '',
      email: '',
      photo: ''
    }]);
  };

  const removeCollaborator = (index) => {
    const newCollaborators = collaborators.filter((_, i) => i !== index);
    setCollaborators(newCollaborators);
  };

  const updateCollaborator = (index, field, value) => {
    const newCollaborators = [...collaborators];
    newCollaborators[index][field] = value;
    setCollaborators(newCollaborators);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1a365d] mb-2">Paramètres du Site</h1>
        <p className="text-gray-600">Gérez le logo, les images, les contacts et l'équipe</p>
      </div>

      <div className="space-y-8">
        {/* Site Information */}
        <Card className="p-6">
          <h2 className="text-2xl font-bold text-[#1a365d] mb-6">Informations du Site</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nom du site</Label>
                <Input
                  value={settings.site_name}
                  onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Email de contact</Label>
                <Input
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Téléphone 1</Label>
                <Input
                  value={settings.contact_phone_1}
                  onChange={(e) => setSettings({ ...settings, contact_phone_1: e.target.value })}
                />
              </div>
              <div>
                <Label>Téléphone 2</Label>
                <Input
                  value={settings.contact_phone_2}
                  onChange={(e) => setSettings({ ...settings, contact_phone_2: e.target.value })}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Réseaux Sociaux */}
        <Card className="p-6">
          <h2 className="text-2xl font-bold text-[#1a365d] mb-6">Réseaux Sociaux</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Facebook (URL complète)</Label>
                <Input
                  value={settings.facebook_url}
                  onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })}
                  placeholder="https://facebook.com/buyit"
                />
              </div>
              <div>
                <Label>Instagram (URL complète)</Label>
                <Input
                  value={settings.instagram_url}
                  onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
                  placeholder="https://instagram.com/buyit"
                />
              </div>
              <div>
                <Label>LinkedIn (URL complète)</Label>
                <Input
                  value={settings.linkedin_url}
                  onChange={(e) => setSettings({ ...settings, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/company/buyit"
                />
              </div>
              <div>
                <Label>Twitter/X (URL complète)</Label>
                <Input
                  value={settings.twitter_url}
                  onChange={(e) => setSettings({ ...settings, twitter_url: e.target.value })}
                  placeholder="https://twitter.com/buyit"
                />
              </div>
              <div>
                <Label>WhatsApp (Numéro avec code pays)</Label>
                <Input
                  value={settings.whatsapp_phone}
                  onChange={(e) => setSettings({ ...settings, whatsapp_phone: e.target.value })}
                  placeholder="+972523570058"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Images */}
        <Card className="p-6">
          <h2 className="text-2xl font-bold text-[#1a365d] mb-6">Images du Site</h2>
          
          <div className="space-y-6">
            {/* Logo */}
            <div>
              <Label className="text-lg font-semibold mb-3 block">Logo du Site</Label>
              <div className="flex items-start space-x-4">
                {settings.logo_url && (
                  <img src={settings.logo_url} alt="Logo" className="h-20 object-contain bg-gray-100 p-2 rounded" />
                )}
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'logo_url')}
                    className="mb-2"
                  />
                  <p className="text-sm text-gray-500">Format recommandé: PNG transparent, 400x200px</p>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div>
              <Label className="text-lg font-semibold mb-3 block">Image Hero (Page d'accueil)</Label>
              <div className="flex items-start space-x-4">
                {settings.hero_image_url && (
                  <img src={settings.hero_image_url} alt="Hero" className="h-32 object-cover rounded" />
                )}
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'hero_image_url')}
                    className="mb-2"
                  />
                  <p className="text-sm text-gray-500">Format recommandé: 1920x1080px (paysage)</p>
                </div>
              </div>
            </div>

            {/* Team Image */}
            <div>
              <Label className="text-lg font-semibold mb-3 block">Photo d'Équipe</Label>
              <div className="flex items-start space-x-4">
                {settings.team_image_url && (
                  <img src={settings.team_image_url} alt="Team" className="h-32 object-cover rounded" />
                )}
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'team_image_url')}
                    className="mb-2"
                  />
                  <p className="text-sm text-gray-500">Photo de votre équipe, 1200x800px</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Team Members */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-[#1a365d]">Collaborateurs</h2>
            <Button onClick={addCollaborator} className="bg-[#1a365d] hover:bg-[#d4af37]">
              + Ajouter un Collaborateur
            </Button>
          </div>

          <div className="space-y-6">
            {collaborators.map((collab, index) => (
              <Card key={collab.id} className="p-6 bg-gray-50">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold">Collaborateur {index + 1}</h3>
                  {collaborators.length > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeCollaborator(index)}
                      className="text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nom (English)</Label>
                    <Input
                      value={collab.name}
                      onChange={(e) => updateCollaborator(index, 'name', e.target.value)}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label>Nom (עברית)</Label>
                    <Input
                      value={collab.name_he}
                      onChange={(e) => updateCollaborator(index, 'name_he', e.target.value)}
                      placeholder="שם בעברית"
                    />
                  </div>
                  <div>
                    <Label>Nom (Русский)</Label>
                    <Input
                      value={collab.name_ru}
                      onChange={(e) => updateCollaborator(index, 'name_ru', e.target.value)}
                      placeholder="Имя на русском"
                    />
                  </div>
                  <div>
                    <Label>Poste (English)</Label>
                    <Input
                      value={collab.role}
                      onChange={(e) => updateCollaborator(index, 'role', e.target.value)}
                      placeholder="Director"
                    />
                  </div>
                  <div>
                    <Label>Poste (Français)</Label>
                    <Input
                      value={collab.role_fr}
                      onChange={(e) => updateCollaborator(index, 'role_fr', e.target.value)}
                      placeholder="Directeur"
                    />
                  </div>
                  <div>
                    <Label>Poste (עברית)</Label>
                    <Input
                      value={collab.role_he}
                      onChange={(e) => updateCollaborator(index, 'role_he', e.target.value)}
                      placeholder="תפקיד"
                    />
                  </div>
                  <div>
                    <Label>Téléphone</Label>
                    <Input
                      value={collab.phone}
                      onChange={(e) => updateCollaborator(index, 'phone', e.target.value)}
                      placeholder="+972 52 123 4567"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={collab.email}
                      onChange={(e) => updateCollaborator(index, 'email', e.target.value)}
                      placeholder="email@buyit.il"
                    />
                  </div>
                </div>

                {/* Photo */}
                <div className="mt-4">
                  <Label>Photo du Collaborateur</Label>
                  <div className="flex items-center space-x-4 mt-2">
                    {collab.photo && (
                      <img src={collab.photo} alt={collab.name} className="w-20 h-20 object-cover rounded-full" />
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleCollaboratorPhotoUpload(e, index)}
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/dashboard')}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSaveSettings}
            disabled={loading}
            className="bg-[#1a365d] hover:bg-[#d4af37] px-8"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Sauvegarde...' : 'Sauvegarder Tous les Paramètres'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
